import {
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { CaretDown, CaretUp, ChartBar, TreeStructure } from "@phosphor-icons/react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import EmissionsIcicle from "./EmissionsIcicle";
import EmissionsTree from "./EmissionsTree";
import { scopeThreeCategories } from "../../CompanyEmissionsDashboard/components/helpers";
import LocalSpinner from "../../../../components/LocalSpinner";
import { SCOPE_COLORS } from "./KpiCharts";
import { useTranslation } from "react-i18next";
import { useGetCompanyDimensionReports, useGetCompanyTotalEmissionsReports } from "../../../../hooks/query/reports";
import { useGetOrganizationStructure } from "../../../../hooks/query";
import { useGetDimensionsHierarchy } from "../../../../hooks/query/dimensions";

// Purchased-electricity types for the Scope 2 third layer. `renewableShare`
// drives the market-based split: a type's share of the market-based total is
// its consumption weight scaled by how non-renewable it is (so Certified
// Renewable is ~zero market-based), mirroring the Scope 2 detail page.
const SCOPE2_PURCHASED_TYPES = [
  { name: "Standard Grid",         weight: 0.48, renewableShare: 0.12 },
  { name: "Supplier Contracted",   weight: 0.27, renewableShare: 0.30 },
  { name: "Certified Renewable",   weight: 0.18, renewableShare: 1.00 },
  { name: "Direct / Private Wire", weight: 0.07, renewableShare: 0.55 },
];

// Split a Scope 2 total across the purchased types for the chosen basis.
const buildScope2TypeChildren = (total, basis) => {
  if (!(total > 0)) return [];
  const weightOf = (t) => (basis === "market" ? t.weight * (1 - t.renewableShare) : t.weight);
  const wSum = SCOPE2_PURCHASED_TYPES.reduce((a, t) => a + weightOf(t), 0) || 1;
  return SCOPE2_PURCHASED_TYPES
    .map((t) => ({ name: t.name, value: total * (weightOf(t) / wSum) }))
    .filter((c) => c.value > 0);
};

// Build the {name, value, children} hierarchy that EmissionsIcicle expects.
// Mirrors the data shape used by the sunburst / treemap so all four tabs
// stay in sync as the per-scope data evolves. `scope2Basis` ("location" |
// "market") selects which Scope 2 total feeds the branch; its third layer is
// the purchased-electricity-type split, not the location/market split.
const buildIcicleTree = (currentYearData, scope3Data, t, scope2Basis = "location") => {
  const detail = currentYearData?.scopeOneAndTwoDetail;

  const buildSubcat = (name, items) => {
    const children = (items || [])
      .filter((it) => it.emissions > 0)
      .map((it) => ({ name: it.consumptionType || "—", value: it.emissions }));
    return children.length > 0 ? { name, children } : null;
  };

  const scope1Kids = [
    buildSubcat("Stationary", detail?.stationary),
    buildSubcat("Mobile", detail?.mobile),
    buildSubcat("Refrigerant", detail?.refrigerant),
  ].filter(Boolean);

  const locTotal = currentYearData?.scope2LocationTotal;
  const mktTotal = currentYearData?.scope2MarketTotal;

  const scope3Kids = scopeThreeCategories
    .map((cat) => {
      const v = scope3Data?.[`category${cat.numberKey}TotalCo2Emissions`] || 0;
      return v > 0 ? { name: `${cat.index}. ${t(cat.translationKey)}`, value: v } : null;
    })
    .filter(Boolean);

  // Build a first-level Scope 2 box for a basis (location/market), split by
  // purchased-electricity type. Location-based and market-based are NEVER
  // cumulative — they're alternate calculations — so they're separate sibling
  // boxes, never nested under one summing "Scope 2" node.
  const scope2Box = (basis) => {
    const total = basis === "market" ? mktTotal : locTotal;
    const children = buildScope2TypeChildren(total, basis);
    if (!children.length) return null;
    return { name: `Scope 2 (${basis === "market" ? "Market-based" : "Location-based"})`, children };
  };

  const kids = [];
  if (scope1Kids.length) kids.push({ name: "Scope 1", children: scope1Kids });
  if (scope2Basis === "both") {
    // Tree: both bases as separate first-level boxes — user picks one to drill.
    const loc = scope2Box("location");
    const mkt = scope2Box("market");
    if (loc) kids.push(loc);
    if (mkt) kids.push(mkt);
  } else {
    const box = scope2Box(scope2Basis);
    if (box) kids.push(box);
  }
  if (scope3Kids.length) kids.push({ name: "Scope 3", children: scope3Kids });
  return { name: "Total", children: kids };
};

// Pill toggle for switching between the icicle and tree views — mirrors
// the Summary / Visual ToggleButton at the top of the page so the
// interaction looks consistent across the insights surface.
const ScopeViewToggle = ({ value, onChange }) => {
  const options = [
    { value: "icicle", label: "Icicle", icon: <ChartBar size={18} /> },
    { value: "tree", label: "Tree", icon: <TreeStructure size={18} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <Button
            key={opt.value}
            size="sm"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={opt.icon}
            onClick={() => onChange(opt.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={5}
          >
            {opt.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Pill toggle for the Scope 2 accounting basis used in the breakdown.
const Scope2BasisToggle = ({ value, onChange }) => {
  const options = [
    { value: "location", label: "Location" },
    { value: "market", label: "Market" },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <Button
            key={opt.value}
            size="sm"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            onClick={() => onChange(opt.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={4}
          >
            {opt.label}
          </Button>
        );
      })}
    </Flex>
  );
};

const ScopeTab = ({ currentYearData, scope3Data, isFetchingCurrentYear, isFetchingScope3 }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });
  const [scope2Basis, setScope2Basis] = useState("location");
  const [view, setView] = useState("icicle");

  // Icicle uses the Location/Market basis toggle (single Scope 2 branch). The
  // tree instead branches Scope 2 into both bases as first-level boxes, so it
  // needs no toggle.
  const icicleData = useMemo(
    () => buildIcicleTree(currentYearData, scope3Data, t, scope2Basis),
    [currentYearData, scope3Data, t, scope2Basis]
  );
  const treeData = useMemo(
    () => buildIcicleTree(currentYearData, scope3Data, t, "both"),
    [currentYearData, scope3Data, t]
  );
  const data = view === "tree" ? treeData : icicleData;

  if (isFetchingCurrentYear || isFetchingScope3) return <LocalSpinner />;
  if (!data?.children || data.children.length === 0) {
    return (
      <Flex align="center" justify="center" h="200px" color="gray.400">
        <Text>No emissions data available.</Text>
      </Flex>
    );
  }
  return (
    <Flex direction="column" gap={3}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Box>
          <Text fontSize="body.m" fontWeight="600" color="gray.700">
            Emissions by scope
          </Text>
          <Text fontSize="body.xs" color="gray.500">
            {view === "tree"
              ? "Hierarchical tCO2e split across Scope 1, 2 and 3. Scope 2 branches into location-based and market-based, each split by purchased-electricity type."
              : `Hierarchical tCO2e split across Scope 1, 2 and 3 categories. Scope 2 uses the ${scope2Basis === "market" ? "market-based" : "location-based"} total, split by purchased-electricity type.`}
          </Text>
        </Box>
        <HStack spacing={3}>
          {view === "icicle" && <Scope2BasisToggle value={scope2Basis} onChange={setScope2Basis} />}
          <ScopeViewToggle value={view} onChange={setView} />
        </HStack>
      </Flex>
      {view === "icicle" ? <EmissionsIcicle data={data} /> : <EmissionsTree data={data} />}
      <Text fontSize="body.xs" color="gray.500">
        Big segments are your biggest emissions sources. Scope 3 typically
        dominates — drill in to see which categories are driving the total.
      </Text>
    </Flex>
  );
};

const SCOPES = [
  { key: "scope1", label: "Scope 1", color: SCOPE_COLORS.scope1.base },
  { key: "scope2", label: "Scope 2", color: SCOPE_COLORS.scope2.base },
  { key: "scope3", label: "Scope 3", color: SCOPE_COLORS.scope3.base },
];

// ESG-3960: `id` stays the display NAME because it is the chart's visible label and the exclusion pill
// text. `itemId` carries the real dimension item id, and everything that has to be unambiguous — drilling
// in, and the persisted exclusion set — uses that instead. An org can hold two ACTIVE items sharing a
// name in one dimension, and resolving a click by name picked whichever the catalogue listed first, so
// drilling into one twin could show the other's children.
const buildDimensionData = (data) => {
  const content = data?.content || [];
  const items = content.filter((item) => !item.unattributed && item.totalCo2Emissions > 0);
  if (items.length === 0) return [];

  return items.map((item) => ({
    id: item.dimensionItem,
    itemId: item.dimensionItemId,
    scope1: item.scopeOneTotalCo2Emissions || 0,
    scope2: item.scopeTwoTotalCo2Emissions || 0,
    scope3: item.scopeThreeTotalCo2Emissions || 0,
  }));
};

const describeArc = (innerR, outerR, startAngle, endAngle) => {
  if (outerR <= innerR || outerR <= 0) return "";
  const sa = startAngle - Math.PI / 2;
  const ea = endAngle - Math.PI / 2;
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${outerR * Math.cos(sa)} ${outerR * Math.sin(sa)}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerR * Math.cos(ea)} ${outerR * Math.sin(ea)}`,
    `L ${innerR * Math.cos(ea)} ${innerR * Math.sin(ea)}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerR * Math.cos(sa)} ${innerR * Math.sin(sa)}`,
    "Z",
  ].join(" ");
};

const StackedRadialBar = ({ data, onItemClick, visibleScopes }) => {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Reserve horizontal gutter inside the viewBox for the per-segment
  // labels. The chart itself is a square sized by `size`; we widen the
  // viewBox by `labelGutter * 2` so the longest label (e.g. "Cargo
  // Network") doesn't get clipped by the SVG's left edge when the chart
  // is left-aligned in its container.
  const size = 520;
  const labelGutter = 120;
  const cx = labelGutter + size / 2;
  const cy = size / 2;
  const outerRadius = size / 2 - 70;
  const innerRadius = outerRadius * 0.2;

  const n = data.length;
  const angleStep = (2 * Math.PI) / n;
  const padAngle = 0.04;

  const maxTotal = useMemo(
    () => Math.max(...data.map((d) => d.scope1 + d.scope2 + d.scope3), 1),
    [data]
  );

  const radiusScale = useCallback(
    (value) => innerRadius + (value / maxTotal) * (outerRadius - innerRadius),
    [innerRadius, outerRadius, maxTotal]
  );

  const handleMouse = (e, item, scope) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const total = item.scope1 + item.scope2 + item.scope3;
    const value = item[scope.key];
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      item: item.id,
      scope: scope.label,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
      color: scope.color,
    });
  };

  return (
    <Box position="relative" width="100%" maxW="760px">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${size + labelGutter * 2} ${size}`}
        width="100%"
        style={{ overflow: "visible", display: "block" }}
      >
        <g transform={`translate(${cx},${cy})`}>
          {[0.25, 0.5, 0.75, 1].map((frac) => (
            <circle
              key={frac}
              r={innerRadius + frac * (outerRadius - innerRadius)}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth={0.5}
            />
          ))}

          {data.map((item, i) => {
            const sa = i * angleStep + padAngle / 2;
            const ea = (i + 1) * angleStep - padAngle / 2;

            const v1 = item.scope1;
            const v2 = item.scope2;
            const v3 = item.scope3;

            const r0 = innerRadius;
            const r1 = radiusScale(v1);
            const r2 = radiusScale(v1 + v2);
            const r3 = radiusScale(v1 + v2 + v3);

            const midAngle = (sa + ea) / 2 - Math.PI / 2;
            const labelR = outerRadius + 14;
            const lx = labelR * Math.cos(midAngle);
            const ly = labelR * Math.sin(midAngle);
            const textAnchor = lx > 1 ? "start" : lx < -1 ? "end" : "middle";

            const handleClick = onItemClick ? () => onItemClick(item) : undefined;
            return (
              <g key={item.id} onClick={handleClick}>
                {v1 > 0 && (
                  <path
                    d={describeArc(r0, r1, sa, ea)}
                    fill={SCOPE_COLORS.scope1.base}
                    onMouseEnter={(e) => handleMouse(e, item, SCOPES[0])}
                    onMouseMove={(e) => handleMouse(e, item, SCOPES[0])}
                    onMouseLeave={() => setTooltip(null)}
                    style={{ cursor: "pointer" }}
                  />
                )}
                {v2 > 0 && (
                  <path
                    d={describeArc(r1, r2, sa, ea)}
                    fill={SCOPE_COLORS.scope2.base}
                    onMouseEnter={(e) => handleMouse(e, item, SCOPES[1])}
                    onMouseMove={(e) => handleMouse(e, item, SCOPES[1])}
                    onMouseLeave={() => setTooltip(null)}
                    style={{ cursor: "pointer" }}
                  />
                )}
                {v3 > 0 && (
                  <path
                    d={describeArc(r2, r3, sa, ea)}
                    fill={SCOPE_COLORS.scope3.base}
                    onMouseEnter={(e) => handleMouse(e, item, SCOPES[2])}
                    onMouseMove={(e) => handleMouse(e, item, SCOPES[2])}
                    onMouseLeave={() => setTooltip(null)}
                    style={{ cursor: "pointer" }}
                  />
                )}
                <text
                  x={lx}
                  y={ly}
                  textAnchor={textAnchor}
                  dominantBaseline="central"
                  fontSize={11}
                  fill="#4A5568"
                  fontWeight="500"
                  style={{ cursor: handleClick ? "pointer" : "default" }}
                >
                  {item.id}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {tooltip && (
        <Box
          position="absolute"
          left={`${tooltip.x + 12}px`}
          top={`${tooltip.y - 10}px`}
          bg="white"
          p="8px 12px"
          border="1px solid #ccc"
          borderRadius="4px"
          boxShadow="sm"
          pointerEvents="none"
          zIndex={10}
        >
          <Flex align="center" gap={2}>
            <Box w="10px" h="10px" borderRadius="2px" bg={tooltip.color} />
            <Text fontSize="sm" fontWeight="bold" color="gray.700">
              {tooltip.scope} — {tooltip.item}
            </Text>
          </Flex>
          <Text fontSize="sm" color="gray.600">
            {tooltip.value?.toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO2e ({tooltip.percentage?.toFixed(1)}%)
          </Text>
        </Box>
      )}

      <Flex justify="center" gap={4} mt={2}>
        {SCOPES.filter((s) => !visibleScopes || visibleScopes.has(s.key)).map((s) => (
          <Flex key={s.key} align="center" gap={1.5}>
            <Box w="10px" h="10px" borderRadius="2px" bg={s.color} />
            <Text fontSize="xs" color="gray.600" fontWeight="600">{s.label}</Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

const DimensionRadialBar = ({ selectedYear, quarter }) => {
  // null = whole year; the dimension-items report endpoint requires a non-null quarter ("ALL").
  const reportQuarter = quarter || "ALL";
  const { data: orgStructure, isLoading: isLoadingOrg } = useGetOrganizationStructure({
    cacheTime: 60 * 1000 * 5,
  });

  // The org's dimensions are arranged in levels (Country → Division → Unit
  // …). We track the level key alongside id/name so we can resolve the
  // "next" level when the user drills into a segment. `items` is the full
  // catalogue at that level — we use it to look up children for drill-down.
  const dimensions = useMemo(() => {
    if (!orgStructure?.dimensions) return [];
    return Object.entries(orgStructure.dimensions)
      .filter(([, dim]) => dim?.id && dim?.name)
      .map(([key, dim]) => ({
        id: dim.id,
        name: dim.name,
        levelKey: key,
        items: dim.items || [],
      }));
  }, [orgStructure]);

  // Drill path: breadcrumb of clicked items. Each entry has the item's numeric id (used by the hierarchy
  // endpoint to scope descendants) and its display name (the breadcrumb label).
  //
  // ESG-3960: descendant filtering now matches report rows by id. It used to match by NAME because the
  // DTO carried nothing else, which mis-resolved same-name twins in both directions.
  // Empty = at the originally-selected dimension level showing every item.
  const [drillPath, setDrillPath] = useState([]);

  // Chosen dimension level — persisted per browser session so returning to
  // the tab remembers what the user was looking at.
  const DIMENSION_STORAGE_KEY = "emissionsBreakdown.selectedDimensionId";
  const [selectedDimension, setSelectedDimension] = useState(null);
  // null = nothing explicitly deselected → show all; a Set = items whose pills are currently inactive.
  // Keyed by dimension item id (ESG-3960 — it used to be the display name, so excluding one of two
  // same-name items hid both) rather than a whitelist, so items added mid-session default to visible.
  // Persisted per dimension level in sessionStorage so exclusions survive tab
  // switches, accidental refreshes, and re-visits within the same session.
  const [excludedIds, setExcludedIds] = useState(new Set());
  // The per-item pill list can be long for dimensions like Legal entity with
  // 100+ values, so it rolls up by default.
  const [filterOpen, setFilterOpen] = useState(false);

  // v2: the stored set changed from names to ids in ESG-3960, and a name in an id-keyed set silently
  // excludes nothing. Bumping the key discards the old sets instead of half-applying them.
  const storageKey = selectedDimension?.id
    ? `emissionsBreakdown.dimensionExclusions.v2.${selectedDimension.id}`
    : null;

  // When the selected dimension changes, restore its previous exclusions
  // (or reset to none if this dimension has never been filtered).
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      const arr = raw ? JSON.parse(raw) : [];
      setExcludedIds(new Set(Array.isArray(arr) ? arr : []));
    } catch {
      setExcludedIds(new Set());
    }
  }, [storageKey]);

  // Persist on every change.
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify([...excludedIds]));
    } catch {
      // sessionStorage can throw in private mode; silently ignore.
    }
  }, [storageKey, excludedIds]);

  // Auto-select once dimensions load: restore the session-stored choice if
  // it's still a valid dimension, otherwise default to the first one.
  useEffect(() => {
    if (selectedDimension || dimensions.length === 0) return;
    let stored = null;
    try {
      stored = sessionStorage.getItem(DIMENSION_STORAGE_KEY);
    } catch {
      // ignore
    }
    const match = stored ? dimensions.find((d) => String(d.id) === stored) : null;
    setSelectedDimension(match || dimensions[0]);
  }, [dimensions, selectedDimension]);

  // Switching dimension levels restores whatever exclusions were previously
  // set for that level (or an empty set if it's the first visit). Handled by
  // the restore effect above — no imperative reset needed here. We always
  // reset the drill path because the chosen level becomes the new root.
  const handleDimensionChange = (dim) => {
    setSelectedDimension(dim);
    setDrillPath([]);
    try {
      sessionStorage.setItem(DIMENSION_STORAGE_KEY, String(dim.id));
    } catch {
      // ignore
    }
  };

  // Drilling into a clicked segment requires (1) a deeper dimension level
  // exists, and (2) we can resolve the clicked item's numeric id. The id
  // comes from the org-structure's items list at the current effective
  // level (depth 0) or from the hierarchy response (depth ≥ 1).
  const drillInto = (item) => {
    const startIdx = dimensions.findIndex((d) => d.id === selectedDimension?.id);
    if (startIdx < 0) return;
    const nextIdx = startIdx + drillPath.length + 1;
    if (nextIdx >= dimensions.length) return; // nothing deeper

    // ESG-3960: resolve the clicked segment by its dimension item id, taken straight off the report row.
    // Falling back to the name keeps older cached payloads (no id) working rather than breaking the drill.
    let pool;
    if (drillPath.length === 0) {
      pool = dimensions[startIdx]?.items || [];
    } else {
      const currentLevelKey = dimensions[startIdx + drillPath.length]?.levelKey;
      pool = hierarchyData?.[currentLevelKey]?.items || [];
    }
    const clicked =
      (item.itemId != null && pool.find((it) => String(it.id) === String(item.itemId))) ||
      pool.find((it) => it.name === item.id);
    if (!clicked) return;
    setDrillPath((prev) => [...prev, { id: clicked.id, name: clicked.name }]);
  };

  const stepUpToBreadcrumb = (idx) => {
    // idx === -1 → back to the starting dimension; otherwise truncate the
    // path so the chart shows the level after `drillPath[idx]`.
    setDrillPath((prev) => prev.slice(0, idx + 1));
  };

  // Resolve the effective dimension to fetch. If the user has drilled in,
  // we follow the level chain in `dimensions` (which is in level order
  // from useGetOrganizationStructure) by `drillPath.length` steps from the
  // explicitly-selected starting dimension.
  const effectiveDimension = useMemo(() => {
    if (!selectedDimension || drillPath.length === 0) return selectedDimension;
    const startIdx = dimensions.findIndex((d) => d.id === selectedDimension.id);
    if (startIdx < 0) return selectedDimension;
    const targetIdx = startIdx + drillPath.length;
    return dimensions[targetIdx] || selectedDimension;
  }, [selectedDimension, dimensions, drillPath.length]);

  // To scope the chart to a clicked item's descendants, we ask the
  // /dimensions/hierarchy endpoint for everything below the deepest drill
  // breadcrumb. That endpoint accepts `id=<dimensionItemId>` in the query
  // string and returns an OrganizationDimensionsDto with items populated
  // only for the deeper dimension levels.
  const drillLeafId = drillPath.length > 0 ? drillPath[drillPath.length - 1].id : null;
  const { data: hierarchyData } = useGetDimensionsHierarchy(
    drillLeafId ? `id=${drillLeafId}` : null,
    { enabled: !!drillLeafId, cacheTime: 60 * 1000 }
  );

  // The effective dimension's level key (e.g. "dimensionTwo") tells us
  // which slot in `hierarchyData` holds the items at the level we're
  // currently viewing — those are the names we use to filter the report
  // rows.
  const allowedChildIds = useMemo(() => {
    if (drillPath.length === 0) return null;
    if (!hierarchyData) return new Set(); // fetch in flight — show nothing rather than the wrong rows
    const startIdx = dimensions.findIndex((d) => d.id === selectedDimension?.id);
    if (startIdx < 0) return null;
    const targetIdx = startIdx + drillPath.length;
    const targetLevelKey = dimensions[targetIdx]?.levelKey;
    const items = hierarchyData?.[targetLevelKey]?.items || [];
    return new Set(items.map((it) => String(it.id)));
  }, [drillPath.length, hierarchyData, dimensions, selectedDimension]);

  const { data: dimensionData, isFetching: isFetchingDimension } = useGetCompanyDimensionReports(
    effectiveDimension?.id,
    { year: selectedYear, quarter: reportQuarter, page: 0, size: 100 },
    { enabled: !!effectiveDimension?.id && !!selectedYear }
  );

  const allItems = useMemo(() => {
    const items = buildDimensionData(dimensionData);
    // Exclude any row whose label matches a breadcrumb step. The dimension
    // report sometimes emits a phantom row labelled with the parent's name
    // for emissions dimensioned only at the parent's level — drilling in
    // shouldn't surface that row again.
    const breadcrumbIds = new Set(drillPath.map((step) => String(step.id)));
    let filtered = items.filter((it) => !breadcrumbIds.has(String(it.itemId)));
    if (allowedChildIds) {
      filtered = filtered.filter((it) => allowedChildIds.has(String(it.itemId)));
    }
    return filtered;
  }, [dimensionData, allowedChildIds, drillPath]);

  // Active scope filters — same shape as the other tabs' scope toggle pills.
  // Zeroing out a scope on each row hides its band in the radial bar; we
  // also drop any row whose remaining total is zero so the chart's empty
  // wedges shrink instead of leaving gaps.
  const [visibleScopes, setVisibleScopes] = useState(
    () => new Set(SCOPES.map((s) => s.key))
  );
  const toggleScope = (key) => {
    setVisibleScopes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const chartData = useMemo(() => {
    return allItems
      .filter((item) => !excludedIds.has(String(item.itemId)))
      .map((item) => ({
        ...item,
        scope1: visibleScopes.has("scope1") ? item.scope1 : 0,
        scope2: visibleScopes.has("scope2") ? item.scope2 : 0,
        scope3: visibleScopes.has("scope3") ? item.scope3 : 0,
      }))
      .filter((item) => item.scope1 + item.scope2 + item.scope3 > 0);
  }, [allItems, excludedIds, visibleScopes]);

  const toggleItem = (id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setExcludedIds(new Set());
  const deselectAll = () => setExcludedIds(new Set(allItems.map((it) => String(it.itemId))));
  const anyExcluded = excludedIds.size > 0;

  if (isLoadingOrg) return <LocalSpinner />;

  const hasData = chartData.length > 0;

  return (
    <Flex direction="column" gap={2} align="flex-start">
      <Box>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          Emissions by dimension
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          tCO2e split by your organisational dimensions (e.g. Country, Division).
          Click a segment to drill into the next level.
        </Text>
      </Box>
      <Flex gap={2} align="center" flexWrap="wrap">
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            borderRadius="full"
            border="1px solid"
            borderColor="gray.300"
            bg="transparent"
            color="gray.600"
            fontWeight="normal"
            rightIcon={<CaretDown size={14} />}
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
          >
            {selectedDimension?.name || "Select dimension"}
          </MenuButton>
          <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
            {dimensions.map((dim) => (
              <MenuItem
                key={dim.id}
                onClick={() => handleDimensionChange(dim)}
                fontWeight={selectedDimension?.id === dim.id ? "bold" : "normal"}
              >
                {dim.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        {SCOPES.map((s) => {
          const active = visibleScopes.has(s.key);
          return (
            <Tag
              key={s.key}
              size="sm"
              borderRadius="full"
              cursor="pointer"
              border="1px solid"
              borderColor={active ? s.color : "gray.300"}
              bg={active ? `${s.color}1A` : "transparent"}
              color={active ? "gray.800" : "gray.500"}
              fontWeight="600"
              onClick={() => !active && toggleScope(s.key)}
              _hover={{ bg: active ? `${s.color}1A` : "gray.50" }}
              transition="all 0.15s"
            >
              <TagLabel>{s.label}</TagLabel>
              {active && (
                <TagCloseButton
                  onClick={(e) => { e.stopPropagation(); toggleScope(s.key); }}
                />
              )}
            </Tag>
          );
        })}
      </Flex>

      {(drillPath.length > 0 || (selectedDimension && effectiveDimension && selectedDimension.id !== effectiveDimension.id)) && (
        <Flex align="center" gap={1.5} flexWrap="wrap" fontSize="13px" color="gray.600">
          <Text
            as="button"
            color="limeShade.600"
            fontWeight="600"
            _hover={{ color: "limeShade.700" }}
            onClick={() => stepUpToBreadcrumb(-1)}
          >
            {selectedDimension?.name}
          </Text>
          {drillPath.map((step, idx) => (
            <Fragment key={`${step.id}-${idx}`}>
              <Text color="gray.400">›</Text>
              <Text
                as={idx < drillPath.length - 1 ? "button" : "span"}
                color={idx < drillPath.length - 1 ? "limeShade.600" : "gray.700"}
                fontWeight="600"
                _hover={idx < drillPath.length - 1 ? { color: "limeShade.700" } : undefined}
                onClick={idx < drillPath.length - 1 ? () => stepUpToBreadcrumb(idx) : undefined}
              >
                {step.name}
              </Text>
            </Fragment>
          ))}
          {effectiveDimension && (
            <Text color="gray.400" ml={2}>
              Viewing <Text as="span" fontWeight="600">{effectiveDimension.name}</Text>
              {drillPath.length > 0 && ` within ${drillPath[drillPath.length - 1].name}`}
            </Text>
          )}
        </Flex>
      )}

      {drillPath.length === 0 && allItems.length > 0 && selectedDimension && (
        <Text fontSize="12px" color="gray.500">
          Click a segment to drill into the next dimension level.
        </Text>
      )}

      {allItems.length > 0 && (
        <Box>
          <Flex
            as="button"
            align="center"
            gap={2}
            onClick={() => setFilterOpen((v) => !v)}
            color="gray.600"
            _hover={{ color: "gray.800" }}
          >
            <Text fontSize="13px" fontWeight="600">
              Filter items
            </Text>
            <Text fontSize="13px" color="gray.400">
              {anyExcluded
                ? `${allItems.length - excludedIds.size} of ${allItems.length} selected`
                : `${allItems.length} items`}
            </Text>
            {filterOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </Flex>

          {filterOpen && (
            <Flex gap={2} flexWrap="wrap" align="center" mt={3}>
              {allItems.map((item) => {
                // ESG-3960: toggled by item id, labelled by name. Keyed by name, excluding one of two
                // same-name items hid both.
                const itemKey = String(item.itemId);
                const active = !excludedIds.has(itemKey);
                return (
                  <Tag
                    key={itemKey}
                    size="sm"
                    borderRadius="full"
                    cursor="pointer"
                    border="1px solid"
                    borderColor={active ? "lime" : "gray.300"}
                    bg={active ? "lightLime" : "transparent"}
                    color={active ? "gray.800" : "gray.500"}
                    fontWeight="600"
                    onClick={() => !active && toggleItem(itemKey)}
                    _hover={{ bg: active ? "lightLime" : "gray.50" }}
                    transition="all 0.15s"
                  >
                    <TagLabel>{item.id}</TagLabel>
                    {active && (
                      <TagCloseButton
                        onClick={(e) => { e.stopPropagation(); toggleItem(itemKey); }}
                      />
                    )}
                  </Tag>
                );
              })}
              <Text
                as="button"
                fontSize="13px"
                color="limeShade.600"
                fontWeight="500"
                _hover={{ color: "limeShade.700" }}
                onClick={anyExcluded ? selectAll : deselectAll}
              >
                {anyExcluded ? "Select all" : "Deselect all"}
              </Text>
            </Flex>
          )}
        </Box>
      )}

      {isFetchingDimension && <LocalSpinner />}

      {!isFetchingDimension && hasData && (
        <StackedRadialBar data={chartData} onItemClick={drillInto} visibleScopes={visibleScopes} />
      )}

      {!isFetchingDimension && !hasData && selectedDimension && allItems.length === 0 && (
        <Flex align="center" justify="center" h="400px" color="gray.400">
          <Text>No emissions data available for this dimension.</Text>
        </Flex>
      )}

      {!isFetchingDimension && !hasData && allItems.length > 0 && (
        <Flex align="center" justify="center" h="400px" color="gray.400">
          <Text>No items selected.</Text>
        </Flex>
      )}

      <Text fontSize="body.xs" color="gray.500">
        Bigger arcs are higher emissions. Use the breadcrumb to step back up
        the hierarchy or pick a different dimension to compare locations,
        divisions or units.
      </Text>
    </Flex>
  );
};

const sumEmissions = (items) =>
  (items || []).reduce((sum, item) => sum + (item.emissions || 0), 0);

const ARC_START = 0; // 3:00 position
const ARC_SWEEP = 1.5 * Math.PI; // 270°, ends at 12:00

const describeStrokeArc = (radius, angle) => {
  if (angle <= 0) return "";
  const clamped = Math.min(angle, Math.PI * 2 - 0.001);
  const sa = ARC_START;
  const ea = sa + clamped;
  const largeArc = clamped > Math.PI ? 1 : 0;
  return `M ${radius * Math.cos(sa)} ${radius * Math.sin(sa)} A ${radius} ${radius} 0 ${largeArc} 1 ${radius * Math.cos(ea)} ${radius * Math.sin(ea)}`;
};

export const RadialRingsChart = ({ title, items, unit = "tCO2e", legendScale = 1, onItemClick, activeLabel }) => {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const validItems = items.filter((d) => d.value > 0);
  if (validItems.length === 0) {
    return (
      <Flex direction="column" gap={1}>
        <Text fontSize="sm" fontWeight="bold" color="gray.700">{title}</Text>
        <Flex align="center" justify="center" h="220px" color="gray.300">
          <Text fontSize="sm">No data</Text>
        </Flex>
      </Flex>
    );
  }

  const n = items.length;
  const ringWidth = Math.max(6, Math.min(10, 48 / n));
  const ringGap = Math.max(3, Math.min(5, 20 / n));
  const outerRadius = 80;
  const chartCx = outerRadius + 10;
  const chartCy = outerRadius + 10;
  const svgWidth = 340;
  const svgHeight = (outerRadius + 10) * 2;
  const maxValue = Math.max(...items.map((d) => d.value), 1);

  const handleMouse = (e, item) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      label: item.label,
      value: item.value,
      color: item.color,
    });
  };

  return (
    <Flex direction="column" gap={1}>
      <Flex align="baseline" gap={1}>
        <Text fontSize="sm" fontWeight="bold" color="gray.700">{title}</Text>
        <Text fontSize="xs" color="gray.400">{unit}</Text>
      </Flex>
      <Box position="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          width="100%"
          style={{ overflow: "visible" }}
        >
          <g transform={`translate(${chartCx},${chartCy})`}>
            {items.map((item, i) => {
              const r = outerRadius - i * (ringWidth + ringGap);
              const angle = (item.value / maxValue) * ARC_SWEEP;
              return (
                <g key={item.label}>
                  <path
                    d={describeStrokeArc(r, ARC_SWEEP)}
                    fill="none"
                    stroke="#EDF2F7"
                    strokeWidth={ringWidth}
                    strokeLinecap="round"
                  />
                  {item.value > 0 && (
                    <path
                      d={describeStrokeArc(r, angle)}
                      fill="none"
                      stroke={item.color}
                      strokeWidth={ringWidth}
                      strokeLinecap="round"
                      opacity={activeLabel && activeLabel !== item.label ? 0.25 : 1}
                      onMouseEnter={(e) => handleMouse(e, item)}
                      onMouseMove={(e) => handleMouse(e, item)}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={onItemClick ? () => onItemClick(item) : undefined}
                      style={{ cursor: onItemClick ? "pointer" : "default" }}
                    />
                  )}
                </g>
              );
            })}
          </g>

          {/* Labels on the right, aligned with top of each ring */}
          {items.map((item, i) => {
            const r = outerRadius - i * (ringWidth + ringGap);
            const y = chartCy - r;
            const labelX = chartCx + outerRadius + 8;
            const rowH = ringWidth + ringGap;
            return (
              <foreignObject
                key={`lbl-${item.label}`}
                x={labelX}
                y={y - rowH / 2}
                width="300"
                height={rowH}
                style={{ overflow: "visible" }}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    height: "100%",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontSize: 10 * legendScale, color: "#4A5568" }}>{item.label}</span>
                  <span
                    style={{
                      width: 14 * legendScale,
                      minWidth: 14 * legendScale,
                      height: Math.max(ringWidth - 2, 4) * legendScale,
                      background: item.color,
                      borderRadius: 4 * legendScale,
                      display: "inline-block",
                    }}
                  />
                </div>
              </foreignObject>
            );
          })}

          {/* Values at 12:00, aligned with centre of each ring */}
          {items.map((item, i) => {
            if (item.value <= 0) return null;
            const r = outerRadius - i * (ringWidth + ringGap);
            return (
              <text
                key={`val-${item.label}`}
                x={chartCx + 10}
                y={chartCy - r}
                dominantBaseline="central"
                textAnchor="start"
                fontSize={8 * legendScale}
                fill="#718096"
                fontWeight="500"
              >
                {item.value.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
              </text>
            );
          })}
        </svg>

        {tooltip && (
          <Box
            position="absolute"
            left={`${tooltip.x + 12}px`}
            top={`${tooltip.y - 10}px`}
            bg="white"
            p="8px 12px"
            border="1px solid #ccc"
            borderRadius="4px"
            boxShadow="sm"
            pointerEvents="none"
            zIndex={10}
          >
            <Flex align="center" gap={2}>
              <Box w="10px" h="10px" borderRadius="2px" bg={tooltip.color} />
              <Text fontSize="sm" fontWeight="bold" color="gray.700">{tooltip.label}</Text>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              {tooltip.value?.toLocaleString("en-GB", { maximumFractionDigits: 2 })} {unit}
              {(() => {
                const total = items.reduce((acc, it) => acc + (it.value || 0), 0);
                if (!total || typeof tooltip.value !== "number") return null;
                const pct = (tooltip.value / total) * 100;
                return (
                  <Text as="span" color="gray.500" ml={2}>
                    · {pct.toFixed(1)}%
                  </Text>
                );
              })()}
            </Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

const RING_PALETTE = [
  "#2D3748", "#D69E2E", "#9F7AEA", "#5EB5F3", "#ECC94B",
  "#25C469", "#FC8181", "#90CDF4", "#F6AD55", "#B794F4",
];

const TypeBreakdown = ({ currentYearData, isFetchingCurrentYear }) => {
  const scopeData = useMemo(() => {
    if (!currentYearData) return [];
    return [
      { label: "Scope 1", value: currentYearData.scope1Total || 0, color: SCOPE_COLORS.scope1.base },
      { label: "Scope 2", value: currentYearData.scope2LocationTotal || 0, color: SCOPE_COLORS.scope2.base },
      { label: "Scope 3", value: currentYearData.scope3Total || 0, color: SCOPE_COLORS.scope3.base },
    ];
  }, [currentYearData]);

  const renewablesData = useMemo(() => {
    if (!currentYearData) return [];
    const detail = currentYearData.scopeOneAndTwoDetail;
    const renewable = currentYearData.renewableEnergy || 0;
    const nonRenewable = currentYearData.nonRenewableEnergy || 0;
    if (renewable > 0 || nonRenewable > 0) {
      return [
        { label: "Renewable", value: renewable, color: "#25C469" },
        { label: "Non-renewable", value: nonRenewable, color: "#2D3748" },
      ];
    }
    // Fallback: sum consumption from electricity detail if available
    const elecItems = detail?.electricity || [];
    const totalConsumption = elecItems.reduce((s, i) => s + (i.consumption || 0), 0);
    if (totalConsumption > 0) {
      return [
        { label: "Renewable", value: 0, color: "#25C469" },
        { label: "Non-renewable", value: totalConsumption, color: "#2D3748" },
      ];
    }
    return [];
  }, [currentYearData]);

  const typeData = useMemo(() => {
    if (!currentYearData) return [];
    const detail = currentYearData.scopeOneAndTwoDetail;
    const byType = {};
    ["stationary", "mobile", "refrigerant", "electricity"].forEach((cat) => {
      (detail?.[cat] || []).forEach((item) => {
        if (!item.consumptionType || !item.emissions) return;
        byType[item.consumptionType] = (byType[item.consumptionType] || 0) + item.emissions;
      });
    });
    return Object.entries(byType)
      .map(([label, value], i) => ({ label, value, color: RING_PALETTE[i % RING_PALETTE.length] }))
      .sort((a, b) => b.value - a.value);
  }, [currentYearData]);

  if (isFetchingCurrentYear) return <LocalSpinner />;

  return (
    <Flex direction="column" gap={3}>
      <Box>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          Emissions by type
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          Three radial views of the same period — by scope, renewable vs
          non-renewable energy, and by individual fuel / consumption type.
        </Text>
      </Box>
      <SimpleGrid columns={3} spacing={4}>
        <RadialRingsChart title="Scope" items={scopeData} />
        <RadialRingsChart title="Renewables" items={renewablesData} unit="kWh" />
        <RadialRingsChart title="Type" items={typeData} />
        <Box />
        <Box />
        <Box />
      </SimpleGrid>
      <Text fontSize="body.xs" color="gray.500">
        Use these three lenses together — a high Scope 1/2 value with low
        renewables share is a strong electrification candidate; a long tail in
        Type often reveals a single fuel that dominates.
      </Text>
    </Flex>
  );
};

const EmissionsBreakdown = ({ currentYearData, isFetchingCurrentYear, selectedYear, quarter, onTabChange }) => {
  // null = whole year; the /reports endpoints require a non-null quarter ("ALL").
  const reportQuarter = quarter || "ALL";
  // Scope 3 per-category totals are served by a different endpoint than the
  // /insights/total headline response, so fetch them alongside.
  const { data: scope3Data, isFetching: isFetchingScope3 } = useGetCompanyTotalEmissionsReports(
    { year: selectedYear, quarter: reportQuarter },
    { enabled: !!selectedYear }
  );

  return (
    <Tabs variant="primary" isLazy onChange={onTabChange}>
      <TabList>
        <Tab>Scope</Tab>
        <Tab>Dimension</Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>
          <ScopeTab
            currentYearData={currentYearData}
            scope3Data={scope3Data}
            isFetchingCurrentYear={isFetchingCurrentYear}
            isFetchingScope3={isFetchingScope3}
          />
        </TabPanel>
        <TabPanel px={0}>
          <DimensionRadialBar selectedYear={selectedYear} quarter={quarter} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default EmissionsBreakdown;
